import React, {createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {appConfig} from '../config';
import {
    validatePasswordResetToken,
    login,
    logout,
    requestPasswordReset,
    completeAccountSetup,
    changePassword,
    LoginResult,
    PasswordResetResult,
    TokenValidationResult,
    validateAccountSetupToken, CompleteAccountSetupResult, ForgotPasswordResult,
} from '../api/AuthApi';
import {apiRequest, checkAuthStatus, setAuthProvider} from "../util/ApiUtils";
import {UserAccount} from "../api/UserAccountApi";
import {ApiResponse, AuthErrorCode} from "../api/types";

interface LoginCredentials {
    username: string;
    password: string;
    stayLoggedIn: boolean;
}

interface AuthContextType {
    userAccount: UserAccount | null;
    isAuthenticated: boolean;
    loading: boolean;
    showReauthModal: boolean;
    login: (credentials: LoginCredentials) => Promise<LoginResult>;
    logout: () => Promise<void>;
    requestPasswordReset: (username: string) => Promise<ForgotPasswordResult>;
    validatePasswordResetToken: (token: string) => Promise<TokenValidationResult>;
    validateAccountSetupToken: (token: string) => Promise<TokenValidationResult>;
    completeAccountSetup: (password: string, confirmedPassword: string, token: string) => Promise<CompleteAccountSetupResult>;
    changePassword: (password: string, confirmedPassword: string, token: string) => Promise<PasswordResetResult>;
    hasAuthority: (role: string) => boolean;
    hasAnyAuthority: (roles: string[]) => boolean;
    handleTokenExpiration: () => void;
    dismissReauthModal: () => void;
    quickReauth: (password: string) => Promise<LoginResult>;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
    children: ReactNode;
    autoRedirectOnExpiration?: boolean;
    tokenCheckInterval?: number;
}

const backendUrl = appConfig.backendUrl;

export const AuthProvider = ({
                                 children,
                                 autoRedirectOnExpiration = false,
                                 tokenCheckInterval = 5 * 60 * 1000
                             }: AuthProviderProps) => {

    const [userAccount, setUserAccount] = useState<UserAccount | null>(null);
    const [isAuthenticated, setAuthenticated] = useState<boolean>(false);
    const [loading, setLoading] = useState(true);
    const [showReauthModal, setShowReauthModal] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const tokenCheckRef = useRef<NodeJS.Timeout | null>(null);

    // Set up auth provider for token expiration handling
    useEffect(() => {
        setAuthProvider({
            handleTokenExpiration: () => handleTokenExpiration()
        });
    }, []);

    // Set up periodic token check
    useEffect(() => {
        if (isAuthenticated && tokenCheckInterval > 0) {
            const startTokenCheck = () => {
                tokenCheckRef.current = setInterval(async () => {
                    const isValid = await checkAuthStatus();
                    if (!isValid) {
                        handleTokenExpiration();
                    }
                }, tokenCheckInterval);
            };

            startTokenCheck();

            return () => {
                if (tokenCheckRef.current) {
                    clearInterval(tokenCheckRef.current);
                }
            };
        }
    }, [isAuthenticated, tokenCheckInterval]);

    // Check authentication on mount
    const checkAuth = async () => {
        try {
            const userData = await apiRequest(`${backendUrl}/api/users/me`);
            if (userData) {
                setUserAccount(userData);
                setAuthenticated(true);
            } else {
                setUserAccount(null);
                setAuthenticated(false);
            }
        } catch (error) {
            setUserAccount(null);
            setAuthenticated(false);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        checkAuth();
    }, []);

    // ============================================================================
    // Token Expiration Handling
    // ============================================================================

    const handleTokenExpiration = useCallback(() => {
        if (tokenCheckRef.current) {
            clearInterval(tokenCheckRef.current);
            tokenCheckRef.current = null;
        }

        const isOnLoginPage = location.pathname.startsWith('/login');

        if (autoRedirectOnExpiration && !isOnLoginPage) {
            setUserAccount(null);
            setAuthenticated(false);
            navigate('/login', {
                replace: true,
                state: {
                    from: location.pathname,
                    message: 'Your session has expired. Please log in again.'
                }
            });
        } else if (!isOnLoginPage) {
            setShowReauthModal(true);
        }
    }, [autoRedirectOnExpiration, navigate, location.pathname]);

    const dismissReauthModal = useCallback(() => {
        setShowReauthModal(false);
        setUserAccount(null);
        setAuthenticated(false);
        navigate('/login', {
            replace: true,
            state: {
                from: location.pathname,
                message: 'Your session has expired. Please log in again.'
            }
        });
    }, [navigate, location.pathname]);

    // ============================================================================
    // Authentication Methods
    // ============================================================================

    const quickReauth = async (password: string): Promise<LoginResult> => {
        if (!userAccount?.username) {
            return {
                success: false,
                errorCode: AuthErrorCode.INVALID_CREDENTIALS
            };
        }

        try {
            setLoading(true);

            const response: ApiResponse<void> = await login({
                username: userAccount.username,
                password: password,
                stayLoggedIn: false
            });

            if (response.success) {
                await checkAuth();
                setShowReauthModal(false);
                return {success: true};
            }

            return {
                success: false,
                errorCode: response.error?.code as AuthErrorCode
            };

        } catch (error) {
            console.error('Quick reauth error:', error);
            return {
                success: false,
                errorCode: AuthErrorCode.UNEXPECTED_ERROR
            };
        } finally {
            setLoading(false);
        }
    };

    const loginUser = async (credentials: LoginCredentials): Promise<LoginResult> => {
        setLoading(true);

        try {
            const response: ApiResponse<void> = await login(credentials);

            if (response.success) {
                const userData = await apiRequest(`${backendUrl}/api/users/me`);
                if (userData) {
                    setShowReauthModal(false);
                    setUserAccount(userData);
                    setAuthenticated(true);
                    return {success: true};
                } else {
                    return {
                        success: false,
                        errorCode: AuthErrorCode.UNEXPECTED_ERROR
                    };
                }
            }

            return {
                success: false,
                errorCode: response.error?.code as AuthErrorCode
            };

        } catch (error) {
            console.error('Login error:', error);
            return {
                success: false,
                errorCode: AuthErrorCode.UNEXPECTED_ERROR
            };
        } finally {
            setLoading(false);
        }
    };

    const logoutUser = async () => {
        try {
            setLoading(true);

            if (tokenCheckRef.current) {
                clearInterval(tokenCheckRef.current);
                tokenCheckRef.current = null;
            }

            await logout();

        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            setUserAccount(null);
            setAuthenticated(false);
            setLoading(false);
            window.location.replace('/login');
        }
    };

    // ============================================================================
    // Password Reset Methods
    // ============================================================================

    const requestPasswordResetMethod = async (username: string): Promise<PasswordResetResult> => {
        setLoading(true);

        try {
            const response: ApiResponse<void> = await requestPasswordReset({username});

            if (response.success) {
                return {success: true};
            }

            return {
                success: false,
                errorCode: response.error?.code as AuthErrorCode
            };

        } catch (error) {
            console.error('Password reset request error:', error);
            return {
                success: false,
                errorCode: AuthErrorCode.UNEXPECTED_ERROR
            };
        } finally {
            setLoading(false);
        }
    };

    const validatePasswordResetTokenMethod = async (token: string): Promise<TokenValidationResult> => {
        setLoading(true);

        try {
            const response: ApiResponse<void> = await validatePasswordResetToken({token});

            if (response.success) {
                return {
                    success: true
                };
            }

            return {
                success: false,
                errorCode: response.error?.code as AuthErrorCode
            };

        } catch (error) {
            console.error('Token validation error:', error);
            return {
                success: false,
                errorCode: AuthErrorCode.UNEXPECTED_ERROR
            };
        } finally {
            setLoading(false);
        }
    };

    const validateAccountSetupTokenMethod = async (token: string): Promise<TokenValidationResult> => {
        setLoading(true);

        try {
            const response: ApiResponse<void> = await validateAccountSetupToken({token});

            if (response.success) {
                return {
                    success: true
                };
            }

            return {
                success: false,
                errorCode: response.error?.code as AuthErrorCode
            };

        } catch (error) {
            console.error('Token validation error:', error);
            return {
                success: false,
                errorCode: AuthErrorCode.UNEXPECTED_ERROR
            };
        } finally {
            setLoading(false);
        }
    };

    const completeAccountSetupMethod = async (
        password: string,
        confirmedPassword: string,
        token: string
    ): Promise<PasswordResetResult> => {
        setLoading(true);

        try {
            const response: ApiResponse<void> = await completeAccountSetup({
                password,
                confirmedPassword,
                token
            });

            if (response.success) {
                return {success: true};
            }

            return {
                success: false,
                errorCode: response.error?.code as AuthErrorCode
            };

        } catch (error) {
            console.error('Complete account setup error:', error);
            return {
                success: false,
                errorCode: AuthErrorCode.UNEXPECTED_ERROR
            };
        } finally {
            setLoading(false);
        }
    };

    const changePasswordMethod = async (
        password: string,
        confirmedPassword: string,
        resetToken: string
    ): Promise<PasswordResetResult> => {
        setLoading(true);

        try {
            const response: ApiResponse<void> = await changePassword({
                password,
                confirmedPassword,
                token: resetToken
            });

            if (response.success) {
                return {success: true};
            }

            return {
                success: false,
                errorCode: response.error?.code as AuthErrorCode
            };

        } catch (error) {
            console.error('Change password error:', error);
            return {
                success: false,
                errorCode: AuthErrorCode.UNEXPECTED_ERROR
            };
        } finally {
            setLoading(false);
        }
    };

    // ============================================================================
    // Authorization Helpers
    // ============================================================================

    const hasAuthority = (role: string) => {
        if (!userAccount) return false;
        return userAccount.authorities && userAccount.authorities.some(a => a.authority === role);
    };

    const hasAnyAuthority = (roles: string[]): boolean => {
        if (!userAccount) return false;
        if (!roles || roles.length === 0) return false;

        return roles.some(role =>
            userAccount.authorities?.some(a => a.authority === role)
        );
    };

    // ============================================================================
    // Provider
    // ============================================================================

    return (
        <AuthContext.Provider value={{
            userAccount,
            isAuthenticated,
            loading,
            showReauthModal,
            login: loginUser,
            logout: logoutUser,
            requestPasswordReset: requestPasswordResetMethod,
            validatePasswordResetToken: validatePasswordResetTokenMethod,
            validateAccountSetupToken: validateAccountSetupTokenMethod,
            completeAccountSetup: completeAccountSetupMethod,
            changePassword: changePasswordMethod,
            hasAuthority,
            hasAnyAuthority,
            handleTokenExpiration,
            dismissReauthModal,
            quickReauth
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === null) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};